// Pieces the timeline pages share: the page shell, the sub line, the "who" picker and frames for previews.
import { faceStyleOf } from "../../../library/faceSets";
import { useRef, type ReactNode } from "react";
import { viewRect } from "../../../engine/camera";
import { faceImages, BASE, type DrawFrame, type DrawImage } from "../../../engine/frame";
import { pictureFit } from "../../../engine/camera";
import { timelineFrameAt } from "../../../engine/timelineFrame";
import { useLibrary } from "../../../library/store";
import type { Camera, Picture } from "../../../model/episode";
import type { Viseme } from "../../../model/face";
import type { Asset, Library } from "../../../model/library";
import { addMarker, feelingAt, markById, pictureAt, placeholderOf, setMarker, type MarkFields, type MarkKind, type PaintSource, type PlacedFace, type PlacedLine, type TimelineEpisode } from "../../../model/timeline";
import { uid } from "../../../model/ids";
import type { Update } from "../episodeStore";
import { Body, DoneFooter, Footer, Layer, Page, Toast, TopBar, Avatar } from "../../common";
import { useTimeline } from "../TimelineLoader";

// 0:04.2
export const stamp = (t: number) => `${Math.floor(t / 60)}:${(t % 60).toFixed(1).padStart(4, "0")}`;
// "From 0:12.4" for something on the timeline, "At 0:12.4" for something being added.
export const subLine = (t: number, editing: boolean) => `${editing ? "From" : "At"} ${stamp(t)}`;

// Every character in the library, in library order.
export const useCharacters = (): Asset[] => useLibrary().assets.filter(a => a.kind === "character");

// Full screen: top bar with back, a scrolling body, and a footer (Done, unless the page gives its own). Everything on a page saves as it is changed, so
// back just leaves.
export function PageShell({ title, t, editing, onClose, footer, toast, clearToast, className, page, children }: { // page: a wizard's step, so the body scrolls to the top when it changes
  title: string; t: number; editing: boolean; onClose: () => void; footer?: ReactNode; toast?: string | null; clearToast?: () => void; className?: string; page?: string; children: ReactNode;
}) {
  return (
    <Layer>
      <Page className={`tl-page${className ? ` ${className}` : ""}`}>
        <TopBar onBack={onClose} title={title} sub={subLine(t, editing)} />
        <Body page={page}>{children}</Body>
        {footer ? <Footer>{footer}</Footer> : <DoneFooter onDone={onClose} />}
        {toast && <Toast onClick={clearToast}>{toast}</Toast>}
      </Page>
    </Layer>
  );
}

// Who: a row of avatars with names. `note` gives a reason a character can't be picked.
export function WhoPicker({ ids, value, onChange, note, label = "Who" }: {
  ids: string[]; value: string | undefined; onChange: (id: string) => void; note?: (id: string) => string | undefined; label?: string;
}) {
  const lib = useLibrary();
  if (!ids.length) return <p className="hint">There are no characters in the library yet.</p>;
  return (
    <div className="tl-who" role="radiogroup" aria-label={label}>
      {ids.map(id => {
        const a = lib.assets.find(x => x.id === id);
        const why = note?.(id);
        return (
          <button key={id} role="radio" aria-checked={value === id} disabled={!!why} className={`tl-who-chip${value === id ? " on" : ""}`} onClick={() => onChange(id)}>
            <Avatar assetId={id} size={52} />
            <b>{a?.name ?? "?"}</b>
            {why && <span>{why}</span>}
          </button>
        );
      })}
    </div>
  );
}

// The frame at t as the timeline shows it, with a different camera if given.
export function frameWith(ep: TimelineEpisode, t: number, clock: number, camera?: Camera): DrawFrame {
  const f = timelineFrameAt(ep, t, clock);
  return camera ? { ...f, view: viewRect(camera) } : f;
}

// A picture with the given faces on it (the ones being placed), drawn through `camera`. `talk`: one character's mouth is open.
export function facesFrame(ep: TimelineEpisode, t: number, clock: number, pic: Picture, faces: PlacedFace[], camera: Camera, talk?: { char: string; viseme: Viseme }): DrawFrame {
  const view = viewRect(camera);
  const fit = pictureFit(pic);
  const images: DrawImage[] = [{ href: pic.src, x: fit.x, y: fit.y, w: pic.w * fit.s, h: pic.h * fit.s }];
  for (const f of faces) images.push(...faceImages(f.face, fit, feelingAt(ep, f.char, t), false, talk?.char === f.char ? talk.viseme : undefined, faceStyleOf(f.char)));
  return { frame: Math.floor(clock * 24), view, base: BASE, boil: null, images };
}

// The characters (library ids) a picture was painted from: its references that are the picture of a character.
export const refCharacterIds = (lib: Library, refs: PaintSource[]): string[] =>
  refs.flatMap(r => (r.kind === "character" ? lib.assets.filter(a => a.kind === "character" && a.picture.src === r.src).map(a => a.id) : []));

// Applies an edit that makes a new marker and returns the result, so the page can select what it made. The new
// item's id is random, so the update reuses the very episode computed here when nothing changed since the page last
// drew (otherwise it applies the edit to the latest episode, and the selection simply finds nothing).
export function commit(update: Update<TimelineEpisode>, ep: TimelineEpisode, edit: (e: TimelineEpisode) => TimelineEpisode): TimelineEpisode {
  const next = edit(ep);
  update(e => (e === ep ? next : edit(e)));
  return next;
}

// Saving a marker from a page as it is changed: the first change adds it at t (or changes `editId`), later ones change
// the same marker, folded into one undo step.
export function useMarkerSave<K extends MarkKind>(kind: K, t: number, editId?: string): (fields: MarkFields<K>) => void {
  const { update } = useTimeline();
  const id = useRef(editId ?? uid(kind === "picture" ? "pk" : kind === "camera" ? "ck" : "fk")).current;
  return fields => update(e => (markById(e, kind, id) ? setMarker(e, kind, id, fields) : addMarker(e, kind, t, fields, id)), { merge: `mark-${id}` });
}

// "Add a line" at t, for a picture whose only line is the untouched 1 s silence it came with: that silence is the line to
// fill, so it is opened instead of adding another. `back`: t is the end of something (the end of the episode, or the
// boundary after a row), so the picture meant is the one just before it. Nothing to fill: undefined.
export function placeholderToFill(ep: TimelineEpisode, t: number, back = false): PlacedLine | undefined {
  const pic = pictureAt(ep, Math.max(0, back ? t - 0.05 : t));
  return pic ? placeholderOf(ep, pic.id) : undefined;
}
